"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount, readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "refrigerator-lifespan-calculator",
  "kitchen-space-planner",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Electricity Cost Estimator",
  category: "Energy & Cost" as const,
  mediumDescription:
    "Add up the monthly and annual electricity cost of every cold appliance in your home — primary and second fridges, freezers, ice makers, and water coolers — at your local rate.",
  faqs: [
    {
      question: "What annual kWh figures are you using?",
      answer:
        "We use 500 kWh/yr for an average refrigerator, 300 kWh/yr for a freezer, ~144 kWh/yr (12 kWh/mo) for a built-in ice maker, and ~720 kWh/yr (60 kWh/mo) for a hot+cold water cooler. These match DOE and ENERGY STAR averages for current generation appliances.",
    },
    {
      question: "Why is the second fridge in the garage so expensive?",
      answer:
        "Garage temperature swings force the compressor to work overtime. Older second fridges from the early 2000s frequently double the kWh figure we use, pushing them past $200/yr in many states.",
    },
    {
      question: "Where do I find my electricity rate?",
      answer:
        "Take the total dollar amount on your latest bill and divide by total kWh used. The US average is around 16.5¢/kWh; California is closer to 30¢, Texas around 14.7¢.",
    },
    {
      question: "Is the water cooler really that bad?",
      answer:
        "Yes. Hot+cold dispensers typically use 60+ kWh/month — more than a full-size refrigerator on a per-cubic-foot basis. Cold-only models cut that roughly in half.",
    },
    {
      question: "How much do ENERGY STAR fridges save versus 10-year-old units?",
      answer:
        "ENERGY STAR estimates that fridges 10+ years old use up to 35% more energy than current ENERGY STAR certified models. The replacement payback is usually 2–4 years on energy savings alone.",
    },
  ],
  relatedTools: ["energy-cost-calculator", "refrigerator-rebate-finder", "refrigerator-lifespan-calculator"],
}

export function UtilityBillEstimatorPageClient() {
  const [numFridges, setNumFridges] = useState("1")
  const [numFreezers, setNumFreezers] = useState("0")
  const [hasIceMaker, setHasIceMaker] = useState(false)
  const [hasWaterCooler, setHasWaterCooler] = useState(false)
  const [electricityRate, setElectricityRate] = useState("0.165")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // An appliance that isn't there adds nothing, so a count below one folds to
    // zero; a $/kWh rate of zero is not a bill, so that one has to be rejected.
    const fridges = readCount(numFridges, { min: 1, max: 20 }) ?? 0
    const freezers = readCount(numFreezers, { min: 1, max: 20 }) ?? 0
    const rate = readQuantity(electricityRate, { min: 0.01, max: 2 })
    if (rate === null) return null

    const breakdown: { appliance: string; kwhPerMonth: number; cost: number }[] = []
    let totalKwhPerMonth = 0

    if (fridges > 0) {
      const fridgeKwhPerMonth = (500 / 12) * fridges
      totalKwhPerMonth += fridgeKwhPerMonth
      breakdown.push({
        appliance: `Refrigerator${fridges > 1 ? "s" : ""} (${fridges})`,
        kwhPerMonth: Math.round(fridgeKwhPerMonth),
        cost: parseFloat((fridgeKwhPerMonth * rate).toFixed(2)),
      })
    }
    if (freezers > 0) {
      const freezerKwhPerMonth = (300 / 12) * freezers
      totalKwhPerMonth += freezerKwhPerMonth
      breakdown.push({
        appliance: `Freezer${freezers > 1 ? "s" : ""} (${freezers})`,
        kwhPerMonth: Math.round(freezerKwhPerMonth),
        cost: parseFloat((freezerKwhPerMonth * rate).toFixed(2)),
      })
    }
    if (hasIceMaker) {
      totalKwhPerMonth += 12
      breakdown.push({ appliance: "Ice maker", kwhPerMonth: 12, cost: parseFloat((12 * rate).toFixed(2)) })
    }
    if (hasWaterCooler) {
      totalKwhPerMonth += 60
      breakdown.push({ appliance: "Water cooler", kwhPerMonth: 60, cost: parseFloat((60 * rate).toFixed(2)) })
    }

    const totalMonthly = parseFloat((totalKwhPerMonth * rate).toFixed(2))
    const totalAnnual = parseFloat((totalMonthly * 12).toFixed(2))
    const percentOfBill = parseFloat(((totalKwhPerMonth / 900) * 100).toFixed(1))

    return { breakdown, totalMonthly, totalAnnual, percentOfBill, totalKwhPerMonth: Math.round(totalKwhPerMonth) }
  }, [numFridges, numFreezers, hasIceMaker, hasWaterCooler, electricityRate])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What does cold storage"
      heroTitleItalic="really"
      heroTitlePost="cost in your house?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "DOE + EIA + ENERGY STAR data"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "500", label: "kWh / fridge yr", note: "DOE avg" },
            { value: "300", label: "kWh / freezer yr", note: "DOE avg" },
            { value: "60", label: "kWh / mo cooler", note: "Hot+cold" },
            { value: "12", label: "kWh / mo ice", note: "Built-in" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-electricity-cost-estimator")}
      cta={{
        title: "Cut the bill. Replace the worst offender.",
        body: "ENERGY STAR refrigerators use up to 35% less energy than 10-year-old units. Browse certified models with the lowest annual kWh.",
        buttonLabel: "Browse ENERGY STAR fridges",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Count every
                <br />
                cold appliance.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Don&apos;t forget the garage fridge and the office water cooler.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Refrigerators
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    step={1}
                    value={numFridges}
                    onChange={(e) => setNumFridges(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Standalone freezers
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    step={1}
                    value={numFreezers}
                    onChange={(e) => setNumFreezers(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  />
                </div>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-[13px] font-bold cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasIceMaker}
                      onChange={(e) => setHasIceMaker(e.target.checked)}
                      className="w-4 h-4"
                    />
                    Ice maker
                  </label>
                  <label className="flex items-center gap-2 text-[13px] font-bold cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasWaterCooler}
                      onChange={(e) => setHasWaterCooler(e.target.checked)}
                      className="w-4 h-4"
                    />
                    Water cooler
                  </label>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Electricity rate
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={0.01}
                      max={2}
                      step="0.01"
                      value={electricityRate}
                      onChange={(e) => setElectricityRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">$/kWh</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> US avg: 0.165 · California: 0.302 · Texas: 0.147
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }}
                />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Annual cold-appliance cost
                  </div>
                  <div
                    className="font-black"
                    style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}
                  >
                    {`$${result?.totalAnnual?.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? 0}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/yr</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Per month</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result?.totalMonthly?.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) ?? "0.00"}`}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">kWh / mo</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.totalKwhPerMonth ?? 0}</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">% of bill</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.percentOfBill ?? 0}%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown table */}
          {result && result.breakdown.length > 0 && (
            <div className="bg-white rounded-xl p-5 md:p-6 border border-gray-200">
              <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-[16px] font-black tracking-[-0.02em] text-gray-900">
                  Per-appliance breakdown
                </h3>
                <span className="text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold">
                  Monthly
                </span>
              </div>
              <div className="space-y-2">
                {result.breakdown.map((row) => (
                  <div key={row.appliance} className="flex items-baseline justify-between border-b border-gray-100 pb-2">
                    <div className="text-[14px] font-semibold text-gray-900">{row.appliance}</div>
                    <div className="flex items-baseline gap-4">
                      <span className="text-[12px] font-mono text-gray-500">{row.kwhPerMonth} kWh</span>
                      <span className="text-[14px] font-bold text-[#0469f0]">{`$${row.cost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Replace the worst offender"
          title="Counter-depth fridges with the lowest annual cost"
          description="Certified models that use up to 35% less energy than 10-year-old units."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                <em className="italic text-[#ff9951]">DOE</em> averages. EIA rates.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Reference numbers come from DOE federal-standards data, ENERGY STAR&apos;s certified product database, and EIA Form-861 utility filings.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Per-appliance kWh",
                  d: "500/yr per fridge, 300/yr per freezer, 144/yr per ice maker, 720/yr per hot+cold water cooler.",
                },
                {
                  n: "02",
                  t: "Multiply by your rate",
                  d: "Total kWh × $/kWh from your bill. We default to the US 16.5¢ EIA national average if you don't have a number handy.",
                },
                {
                  n: "03",
                  t: "Compare to household total",
                  d: "Average US home uses ~900 kWh/month. We surface what % of that is just cold storage.",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges — cheapest to run"
          description="The most efficient style at any size — typically 400–500 kWh/yr."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
